import {
  FiHome,
  FiUsers,
  FiMic,
  FiCreditCard,
  FiPackage,
  FiGift,
  FiPhone,
  FiActivity,
  FiRefreshCw,
  FiTrendingUp,
  FiBell,
  FiChevronDown,
  FiChevronRight,
  FiRadio,
  FiSettings,
  FiFlag,
  FiTag,
  FiMail,
} from "react-icons/fi";
import { MdOutlineRoomPreferences, MdLeaderboard } from "react-icons/md";
import { LuIndianRupee } from "react-icons/lu";
import { getUserRole } from "./utilities/getUserRole";
import { FaHistory } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { db } from "../lib/firebase";
import { collection, query as fsQuery, where, getCountFromServer } from "firebase/firestore";

const navItems = [
  { label: "Dashboard", path: "/", icon: <FiHome /> },
  { label: "Users", path: "/users", icon: <FiUsers /> },
  { label: "Coin Earnings", path: "/coin-earnings", icon: <LuIndianRupee />, showOnlyForEmail: ["admin@arrowcoders.com"] },
  { label: "Store Management", path: "/coin-packages", icon: <FiPackage /> },
  { label: "Coupons", path: "/coupons", icon: <FiTag /> },
  { label: "Gifts", path: "/gifts", icon: <FiGift /> },

  // SUPER ADMIN ONLY
  { 
    label: "Conversion Settings", 
    path: "/conversion-settings", 
    icon: <FiRefreshCw />, 
    showOnlyForEmail: ["admin@arrowcoders.com", "haravindh@arrowcoders.com"] 
  },

  // RESTRICTED ACCESS
  { 
    label: "Call Configurations", 
    path: "/call-config", 
    icon: <FiPhone />, 
    showOnlyForEmail: ["admin@arrowcoders.com", "haravindh@arrowcoders.com"] 
  },

  { label: "Voice Approvals", path: "/voice-approval", icon: <FiMic /> },
  { label: "Withdraw Requests", path: "/withdrawals", icon: <FiCreditCard /> },
  { label: "Call History", path: "/call-transactions", icon: <FiActivity /> },
  { label: "Purchase History", path: "/purchase-history", icon: <FaHistory /> },
  { label: "Chats", path: "/chats", icon: <FiMail />, showOnlyForEmail: ["admin@arrowcoders.com"] },
  { label: "Leader Board", path: "/leaderboard", icon: <MdLeaderboard /> },
  { label: "User Activity", path: "/user-activity", icon: <FiRadio /> },
  { label: "Analytics", path: "/analytics", icon: <FiTrendingUp /> },
  { label: "Reports", path: "/reports", icon: <FiFlag /> },
  { label: "Contact Us", path: "/contact-us", icon: <FiMail /> },
];

const Sidebar = () => {
  const location = useLocation();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [customisationsOpen, setCustomisationsOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  

  // Get user role and email from localstorage utility
  const userData = getUserRole() || {};
  const userRole = userData.role;
  const userEmail = (typeof userData.email === "string" ? userData.email : "").toLowerCase();

  // Check if notifications menu should be open based on current route
  const isNotificationsActive = 
    location.pathname === "/push-notifications" || 
    location.pathname === "/in-app-notifications";

  // Check if customisations menu should be open based on current route
  const isCustomisationsActive = 
    location.pathname === "/customisations";

  const isLeaderboardActive =
    location.pathname === "/leaderboard" ||
    location.pathname.startsWith("/leaderboard/");

  // Auto-open menus if on their respective pages
  useEffect(() => {
    if (isNotificationsActive) {
      setNotificationsOpen(true);
    }
    if (isCustomisationsActive) {
      setCustomisationsOpen(true);
    }
    if (isLeaderboardActive) {
      setLeaderboardOpen(true);
    }
  }, [isNotificationsActive, isCustomisationsActive, isLeaderboardActive]);

  // Pending withdrawals badge
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  useEffect(() => {
    let mounted = true;
    const fetchPending = async () => {
      try {
        const res = await axios.get("/api/v1/app/wallet/withdrawal-requests", {
          params: { status: "PENDING", page: 1, limit: 10 },
        });
        const payload = res?.data || {};
        const list =
          Array.isArray(payload?.data?.withdrawalRequests)
            ? payload.data.withdrawalRequests
            : Array.isArray(payload?.withdrawalRequests)
            ? payload.withdrawalRequests
            : [];
        // If API ignores status param, filter client-side
        const count = list.filter((w) => String(w?.status || "").toUpperCase() === "PENDING").length;
        if (mounted) setPendingWithdrawals(count);
      } catch (_) {
        if (mounted) setPendingWithdrawals(0);
      }
    };
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  const [openContactMessages, setOpenContactMessages] = useState(0);
  useEffect(() => {
    let mounted = true;
    const fetchOpenContacts = async () => {
      try {
        if (!db) return;
        const base = collection(db, "admin_support_chats");
        const openSnap = await getCountFromServer(fsQuery(base, where("status", "==", "OPEN")));
        const count = openSnap.data().count || 0;
        if (mounted) setOpenContactMessages(count);
      } catch (_) {
        if (mounted) setOpenContactMessages(0);
      }
    };
    fetchOpenContacts();
    const t = setInterval(fetchOpenContacts, 30000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  const [pendingVoiceApprovals, setPendingVoiceApprovals] = useState(0);
  useEffect(() => {
    let mounted = true;
    const fetchPending = async () => {
      try {
        const res = await axios.get("/api/v1/app/voice-verification/statistics");
        const payload = res?.data || {};
        const count = Number(payload?.data?.statistics?.verificationStatus?.pending?.count || 0);
        if (mounted) setPendingVoiceApprovals(Number.isFinite(count) ? count : 0);
      } catch (_) {
        if (mounted) setPendingVoiceApprovals(0);
      }
    };
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  const [pendingReports, setPendingReports] = useState(0);
  useEffect(() => {
    let mounted = true;
    const fetchPending = async () => {
      try {
        const res = await axios.get("/api/v1/app/user-reports/counts");
        const count = Number(res?.data?.data?.pendingReports || 0);
        if (mounted) setPendingReports(Number.isFinite(count) ? count : 0);
      } catch (_) {
        if (mounted) setPendingReports(0);
      }
    };
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  return (
    <aside className="fixed top-0 left-0 h-screen w-64
             overflow-y-auto overflow-x-hidden
             flex flex-col justify-between 
             bg-white text-[var(--primary-solid)]
             shadow-xl p-6 border-r-2 border-gray-200">

      <div>
        {/* Logo Section */}
        <div className="flex flex-col items-center gap-3 mb-10">
          <img
            src="/pookielogo.png"
            alt="Admin Panel"
            className="w-20.5 h-20.5 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
          />
          
          
        </div>

        <nav className="flex flex-col gap-2">
          {navItems
            .filter(item => {
              if (item.showOnlyForEmail) {
                const allowedEmails = Array.isArray(item.showOnlyForEmail) 
                  ? item.showOnlyForEmail 
                  : [item.showOnlyForEmail];
                return allowedEmails.some(email => email.toLowerCase() === userEmail);
              }
              return !item.role || item.role === userRole;
            })
            .map((item) => {
              // Skip rendering if it's the notifications placeholder
              if (item.label === "Notifications") return null;
              if (item.label === "Leader Board") {
                const active = isLeaderboardActive;
                return (
                  <div key="leaderboard-menu">
                    <button
                      onClick={() => setLeaderboardOpen(!leaderboardOpen)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all min-w-0 ${
                        active
                          ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                          : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/80"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-lg flex-shrink-0">{item.icon}</span>
                        <span className="truncate">Leader Board</span>
                      </div>
                      {leaderboardOpen ? (
                        <FiChevronDown className="text-lg flex-shrink-0" />
                      ) : (
                        <FiChevronRight className="text-lg flex-shrink-0" />
                      )}
                    </button>

                    {leaderboardOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        <Link
                          to="/leaderboard"
                          className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all min-w-0 ${
                            location.pathname === "/leaderboard"
                              ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                              : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/70"
                          }`}
                        >
                          <span className="text-sm truncate">Leader Board</span>
                        </Link>
                        <Link
                          to="/leaderboard/competitions"
                          className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all min-w-0 ${
                            location.pathname === "/leaderboard/competitions"
                              ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                              : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/70"
                          }`}
                        >
                          <span className="text-sm truncate">Competitions</span>
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }

              const active =
                location.pathname === item.path ||
                location.pathname.startsWith(item.path + "/");

              return (
                <Link
                  to={item.path}
                  key={item.label}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-w-0 ${
                    active
                      ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                      : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/80"
                  }`}
                >
                  <span className="text-lg flex-shrink-0">{item.icon}</span>
                  <span className="truncate flex-1">{item.label}</span>
                  {item.label === "Withdraw Requests" && pendingWithdrawals > 0 && (
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-white text-[var(--primary-solid)]" : "bg-[var(--primary-solid)] text-white"
                    }`}>
                      {pendingWithdrawals}
                    </span>
                  )}
                  {item.label === "Voice Approvals" && pendingVoiceApprovals > 0 && (
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-white text-[var(--primary-solid)]" : "bg-[var(--primary-solid)] text-white"
                    }`}>
                      {pendingVoiceApprovals}
                    </span>
                  )}
                  {item.label === "Contact Us" && openContactMessages > 0 && (
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-white text-[var(--primary-solid)]" : "bg-[var(--primary-solid)] text-white"
                    }`}>
                      {openContactMessages}
                    </span>
                  )}
                  {item.label === "Reports" && pendingReports > 0 && (
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                      active ? "bg-white text-[var(--primary-solid)]" : "bg-[var(--primary-solid)] text-white"
                    }`}>
                      {pendingReports}
                    </span>
                  )}
                </Link>
              );
            })}

          {/* Notifications Menu with Dropdown */}
          <div>
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all min-w-0 ${
                isNotificationsActive
                  ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                  : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/80"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <FiBell className="text-lg flex-shrink-0" />
                <span className="truncate">Notifications</span>
              </div>
              {notificationsOpen ? (
                <FiChevronDown className="text-lg flex-shrink-0" />
              ) : (
                <FiChevronRight className="text-lg flex-shrink-0" />
              )}
            </button>

            {/* Dropdown Submenu */}
            {notificationsOpen && (
              <div className="ml-4 mt-1 space-y-1">
                <Link
                  to="/push-notifications"
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all min-w-0 ${
                    location.pathname === "/push-notifications"
                      ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                      : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/70"
                  }`}
                >
                  <span className="text-sm truncate">Push Notifications</span>
                </Link>
                <Link
                  to="/in-app-notifications"
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all min-w-0 ${
                    location.pathname === "/in-app-notifications"
                      ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                      : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/70"
                  }`}
                >
                  <span className="text-sm truncate">In-App Notifications</span>
                </Link>
              </div>
            )}
          </div>

          {/* Customisations Menu */}
          <div>
            <Link
              to="/customisations"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-w-0 ${
                isCustomisationsActive
                  ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                  : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/80"
              }`}
            >
              <FiSettings className="text-lg flex-shrink-0" />
              <span className="truncate">Customisations</span>
            </Link>
          </div>

          <div>
            <Link
              to="/Room-Managment"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-w-0 ${
                location.pathname === "/Room-Managment" || location.pathname.startsWith("/Room-Managment/")
                  ? "bg-[var(--primary-solid)] text-white font-semibold shadow-md"
                  : "hover:bg-[var(--primary-solid)]/10 hover:text-[var(--primary-solid)] text-[var(--primary-solid)]/80"
              }`}
            >
              <MdOutlineRoomPreferences className="text-lg flex-shrink-0" />
              <span className="truncate">Room Management</span>
            </Link>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;